import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Truck, Save, X, Trash2, Pencil, Info } from "lucide-react";

/**
 * Admin: manage which mail carriers appear at checkout and what each one costs.
 *
 * The list is snapshot-safe — every past order stores its own `shipping_option`
 * copy so editing prices here never rewrites historical invoices.
 */

const empty = {
  code: "",
  name: "",
  carrier: "USPS",
  description: "",
  delivery_days: "2 business days",
  price_usd: 25,
  active: true,
  sort_order: 100,
};

const CARRIERS = ["USPS", "FedEx", "UPS", "DHL", "Other"];

export default function Shipping() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/shipping").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.code || !draft.name || !draft.carrier) {
      toast.error("Code, name and carrier are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/shipping", { ...draft, price_usd: Number(draft.price_usd), sort_order: Number(draft.sort_order) });
      toast.success(`${draft.carrier} · ${draft.name} added`);
      setDraft(empty);
      setCreating(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (opt) => setEditing({ ...opt });

  const saveEdit = async () => {
    setBusy(true);
    try {
      const { id, created_at, updated_at, ...body } = editing;
      body.price_usd = Number(body.price_usd);
      body.sort_order = Number(body.sort_order);
      await api.patch(`/admin/shipping/${id}`, body);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (opt) => {
    try {
      await api.patch(`/admin/shipping/${opt.id}`, { active: !opt.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (opt) => {
    if (!confirm(`Delete ${opt.carrier} · ${opt.name}? Existing orders keep their own shipping snapshot; this only removes it from the checkout page.`)) return;
    try {
      await api.delete(`/admin/shipping/${opt.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-10">
      <div className="label-eyebrow">Fulfillment</div>
      <div className="flex items-center justify-between mt-1 flex-wrap gap-3">
        <h1 className="text-2xl font-bold tracking-tight">Shipping Options</h1>
        {!creating && (
          <button onClick={() => { setDraft(empty); setCreating(true); }} className="btn-primary inline-flex items-center gap-2" data-testid="shipping-add-open">
            <Plus className="h-4 w-4" /> New shipping option
          </button>
        )}
      </div>

      <div className="mt-4 border border-[#D1D5DB] bg-[#F7F7F8] p-4 text-[13px] text-[#5E636E] rounded-md flex items-start gap-3">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-[#003366]" />
        <div>
          Only <b>active</b> options appear at checkout. Every past order keeps a snapshot of the carrier + price it was placed with — editing prices here never rewrites old invoices.
        </div>
      </div>

      {creating && (
        <div className="mt-6 border border-[#D1D5DB] bg-[#F7F7F8] p-5 rounded-md" data-testid="shipping-create-card">
          <div className="flex items-center justify-between mb-3">
            <div className="label-eyebrow flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> New shipping option</div>
            <button onClick={() => setCreating(false)} className="text-[#5E636E] hover:text-black" data-testid="shipping-create-cancel">
              <X className="h-4 w-4" />
            </button>
          </div>
          <Fields draft={draft} setDraft={setDraft} testPrefix="shipping-new" />
          <button onClick={create} disabled={busy} className="btn-primary mt-4 inline-flex items-center gap-2 disabled:bg-[#5E636E]" data-testid="shipping-create-save">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : "Create option"}
          </button>
        </div>
      )}

      <div className="mt-8 border border-[#D1D5DB] overflow-x-auto">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2">Carrier</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Delivery</th>
              <th className="text-left px-4 py-2">Price</th>
              <th className="text-left px-4 py-2">Order</th>
              <th className="text-left px-4 py-2">Active</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-[#5E636E]">No shipping options yet.</td></tr>
            )}
            {items.map((o) => (
              <tr key={o.id} data-testid={`shipping-row-${o.code}`}>
                <td className="px-4 py-3">
                  <div className="inline-flex items-center gap-2">
                    <span className="grid place-items-center h-7 w-7 rounded-md bg-[#003366]/10 text-[#003366]"><Truck className="h-3.5 w-3.5" /></span>
                    <span className="font-semibold">{o.carrier}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div>{o.name}</div>
                  <div className="text-[11px] text-[#5E636E] font-mono">{o.code}</div>
                </td>
                <td className="px-4 py-3 text-sm">{o.delivery_days || "—"}</td>
                <td className="px-4 py-3 font-mono font-semibold">${(o.price_usd || 0).toFixed(2)}</td>
                <td className="px-4 py-3 font-mono text-sm">{o.sort_order}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggleActive(o)}
                    className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border ${o.active ? "border-[#0B6623] text-[#0B6623] bg-[#0B6623]/10" : "border-[#A30414] text-[#A30414] bg-[#A30414]/10"}`}
                    data-testid={`shipping-toggle-${o.code}`}
                  >
                    {o.active ? "Active" : "Off"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap space-x-3">
                  <button onClick={() => startEdit(o)} className="text-xs font-mono uppercase tracking-wider text-[#003366] hover:underline inline-flex items-center gap-1" data-testid={`shipping-edit-${o.code}`}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button onClick={() => remove(o)} className="text-xs font-mono uppercase tracking-wider text-[#A30414] hover:underline inline-flex items-center gap-1" data-testid={`shipping-delete-${o.code}`}>
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" data-testid="shipping-edit-modal">
          <div className="bg-white w-full max-w-2xl border border-[#D1D5DB] rounded-md shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
              <div>
                <div className="label-eyebrow">Edit shipping option</div>
                <div className="font-semibold mt-0.5">{editing.carrier} · <span className="font-mono">{editing.code}</span></div>
              </div>
              <button onClick={() => setEditing(null)} className="text-[#5E636E] hover:text-black" data-testid="shipping-edit-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5">
              <Fields draft={editing} setDraft={setEditing} testPrefix="shipping-edit" />
            </div>
            <div className="px-5 py-4 border-t border-[#E5E7EB] flex items-center justify-end gap-2 bg-[#F7F7F8]">
              <button onClick={() => setEditing(null)} className="text-[12px] font-mono uppercase tracking-wider text-[#5E636E] hover:text-black" data-testid="shipping-edit-cancel">Cancel</button>
              <button onClick={saveEdit} disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:bg-[#5E636E]" data-testid="shipping-edit-save">
                <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Fields({ draft, setDraft, testPrefix }) {
  const set = (k, v) => setDraft({ ...draft, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <label className="label-eyebrow block mb-1">Carrier</label>
        <select value={draft.carrier} onChange={(e) => set("carrier", e.target.value)} className="w-full px-3 py-2 border border-[#D1D5DB] bg-white font-mono text-sm" data-testid={`${testPrefix}-carrier`}>
          {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <F label="Code (unique slug)" v={draft.code} on={(v) => set("code", v)} test={`${testPrefix}-code`} />
      <F label="Display name" v={draft.name} on={(v) => set("name", v)} test={`${testPrefix}-name`} />
      <F label="Delivery description (e.g. 2 business days)" v={draft.delivery_days} on={(v) => set("delivery_days", v)} test={`${testPrefix}-delivery`} />
      <F label="Price (USD)" v={draft.price_usd} on={(v) => set("price_usd", v)} type="number" test={`${testPrefix}-price`} />
      <F label="Sort order (lower = first)" v={draft.sort_order} on={(v) => set("sort_order", v)} type="number" test={`${testPrefix}-sort`} />
      <div className="md:col-span-2">
        <label className="label-eyebrow block mb-1">Longer description (optional)</label>
        <input value={draft.description || ""} onChange={(e) => set("description", e.target.value)} className="w-full px-3 py-2 border border-[#D1D5DB] bg-white text-sm" data-testid={`${testPrefix}-desc`} />
      </div>
      <label className="md:col-span-2 inline-flex items-center gap-2 text-sm cursor-pointer" data-testid={`${testPrefix}-active-wrap`}>
        <input type="checkbox" checked={!!draft.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#003366]" data-testid={`${testPrefix}-active`} />
        Show this option at checkout
      </label>
    </div>
  );
}

const F = ({ label, v, on, test, type = "text" }) => (
  <div>
    <label className="label-eyebrow block mb-1">{label}</label>
    <input type={type} step={type === "number" ? "0.01" : undefined} value={v ?? ""} onChange={(e) => on(e.target.value)} className="w-full px-3 py-2 border border-[#D1D5DB] bg-white font-mono text-sm" data-testid={test} />
  </div>
);
